import { Pages } from '@src/Pages';
import './driveInformationModal.css';
import immediateApi from '@vse/api/immediateApi';

const MINIMUM_WAIT_TIME = 2000;
const MAXIMUM_WAIT_TIME = 17000;

/**
 * Adds the drive information modal to the DOM and returns an object with functions to interact with the modal
 * @returns {{
 *  showDriveInformationModal: (estopReport: Object) => void,
 *  closeDriveInformationModal: () => void
 *  setNavigateFunction: (pNavigate: import('react-router-dom').NavigateFunction) => void
 * }}
 */
export function createDriveInformationModal () {
  let navigate;
  $('.drive-information-modal').remove();

  const lDriveInformationModal = $('<div>')
    .addClass('drive-information-modal')
    .css({ display: 'none' });

  $('body').append(
    lDriveInformationModal,
  );

  const lEnergizingDrivesLoader = $('<div>').addClass('energizing-drives-container').append(
    $('<div>').addClass('energizing-drives-spinner'),
    $('<div>').text('Energizing Drives'),
    $('<div>').text('This may take a moment'),
  );

  let isWaiting = false;
  let hasWaited = false;
  let hasTimedOut = false;
  let lastKnownState;
  let gracePeriodTimeOut;
  let minimumPeriodTimeOut;
  let isModalDisplayed = false;

  /**
   * Updates the drive energy modals' content
   * @param {*} pEstopReport - The estop report published by the execution-engine on the topic execution-engine/estop
   */
  function updateModalContent (pEstopReport) {
    if (pEstopReport.areSmartDrivesReady) {
      lDriveInformationModal.empty();
      clearTimeout(gracePeriodTimeOut);

      const lSuccessWidget = $('<div>').addClass('status-widget');
      const lSuccessIcon = $('<div>').addClass('icon-check-filled success-status-icon');
      const lSuccessTitle = $('<div>').addClass('status-widget-title').text('Energizing Successful');
      const lCloseButton = $('<div>').addClass('force-close-button').text('Close').on('click', closeDriveInformationModal);

      lSuccessWidget.append(
        lSuccessIcon,
        lSuccessTitle,
        lCloseButton,
      );

      lDriveInformationModal.append(lSuccessWidget);

      // Close modal after 2 seconds
      setTimeout(() => {
        closeDriveInformationModal();
      }, 2000);
    }
    else if (hasTimedOut) {
      lDriveInformationModal.empty();

      const lFailureWidget = $('<div>').addClass('status-widget');
      const lFailureTitle = $('<div>').addClass('status-widget-title').text('An issue has occured');
      const lFailureIcon = $('<div>').addClass('icon-warning failure-status-icon');

      const lButtonsContainer = $('<div>').addClass('buttons-container');

      const lClearFaultsButton = $('<button>')
        .addClass('clear-drive-errors-button')
        .append(
          $('<i>').addClass('icon-redo'),
          $('<span>').text('Try Clearing Drive Errors'),
        )
        .on('click', () => {
          lClearFaultsButton.prop('disabled', true);
          immediateApi.tryClearDriveErrors();
          lClearFaultsButton.empty();
          lClearFaultsButton.append($('<div>').addClass('clear-drive-error-spinner'));
          setTimeout(() => {
            lClearFaultsButton.prop('disabled', false);
            lClearFaultsButton.empty();
            lClearFaultsButton.append(
              $('<i>').addClass('icon-redo'),
              $('<span>').text('Try Clearing'),
            );
          }, 2000);
        });

      const lAdjustConfigButton = $('<button>')
        .text('Adjust Configuration')
        .addClass('adjust-config-button')
        .on('click', () => {
          if (window.location.pathname !== Pages.MACHINE_CONFIG) {
            if (navigate === undefined) {
              throw new Error('DriveInformationModal: navigate function not set');
            }
            navigate(Pages.MACHINE_CONFIG);
          }
          closeDriveInformationModal();
        });

      const lDriveStatusContainer = $('<div>').addClass('drive-status-container');
      const lCloseButton = $('<div>').addClass('force-close-button').text('Close').on('click', closeDriveInformationModal);

      if (pEstopReport.detectedNetworks.length > 1) {
        pEstopReport.detectedNetworks.forEach(pControllerDriveStatus => lDriveStatusContainer.append(getDriveStatusWidgetForController(pControllerDriveStatus, true)));
      }
      else {
        lDriveStatusContainer.append(getDriveStatusWidgetForController(pEstopReport.detectedNetworks[0], false));
      }

      lFailureWidget.append(
        lFailureIcon,
        lFailureTitle,
        lDriveStatusContainer,
        lButtonsContainer.append(
          lClearFaultsButton,
          lAdjustConfigButton,
        ),
        lCloseButton,
      );

      lDriveInformationModal.append(lFailureWidget);
    }
  }

  /**
   * Shows the drive energy modal
   * @param {*} pEstopReport - The estop report published by the execution-engine on the topic execution-engine/estop
   */
  function showDriveInformationModal (pEstopReport) {
    // Ignore function call when smartdrives are ready and modal was not displayed.
    if (pEstopReport.areSmartDrivesReady === true && !isModalDisplayed) {
      return;
    }

    lDriveInformationModal.css({ display: 'flex' });

    lastKnownState = pEstopReport;

    if (hasWaited) {
      updateModalContent(pEstopReport);

      return;
    }

    if (!isWaiting) { // it has not waited and it is not waiting, means it needs to start waiting
      lDriveInformationModal.append(lEnergizingDrivesLoader);
      // show loader

      minimumPeriodTimeOut = setTimeout(() => {
        hasWaited = true;
        showDriveInformationModal(lastKnownState);
      }, MINIMUM_WAIT_TIME);

      gracePeriodTimeOut = setTimeout(() => {
        hasTimedOut = true;
        updateModalContent(lastKnownState);
      }, MAXIMUM_WAIT_TIME);

      isWaiting = true;
      isModalDisplayed = true;
    }
  }

  /**
   * Closes the drive energy modal and internally used flags.
   */
  function closeDriveInformationModal () {
    lDriveInformationModal.empty();
    lDriveInformationModal.css({ display: 'none' });
    isWaiting = false;
    hasWaited = false;
    hasTimedOut = false;
    lastKnownState = undefined;
    clearTimeout(gracePeriodTimeOut);
    clearTimeout(minimumPeriodTimeOut);
    isModalDisplayed = false;
  }

  lDriveInformationModal.on('remove', closeDriveInformationModal);

  return {
    showDriveInformationModal,
    closeDriveInformationModal,
    setNavigateFunction: (pNavigate) => { navigate = pNavigate; },
  };
}

const successIcon = () => $('<div>').addClass('icon-check-filled drive-status-icon').css({ color: '#1AC876' });
const failureIcon = () => $('<div>').addClass('icon-close-filled drive-status-icon').css({ color: '#F4B73A' });

const createDriveInformationRow = (pDriveNumber, pDriveEnergized, pMotorDetected, pDriveErrors) => {
  const lDriveErrorMessages = $('<div>').addClass('drive-errors');

  if (pDriveErrors.length !== 0) {
    pDriveErrors.forEach((pDriveError) => {
      lDriveErrorMessages.append(
        $('<div>').text(`• ${pDriveError.description.trim()}`),
      );
    });
  }

  return $('<tr>').append(
    $('<td>').text(pDriveNumber),
    $('<td>').append(pDriveEnergized ? successIcon : failureIcon),
    $('<td>').append(pMotorDetected ? successIcon : failureIcon),
    $('<td>').append(lDriveErrorMessages),
  );
};

/**
 * Returns the drive status widget
 * @param {object} pControllerDriveStatus The drive status object for a given controller
 * @param {boolean} pIncludeControllerName Boolean denoting if the widget should display the controller name or not
 */
function getDriveStatusWidgetForController (pControllerDriveStatus, pIncludeControllerName) {
  const lDriveStatus = $('<div>').addClass('drive-status');

  if (pIncludeControllerName) {
    const lControllerName = $('<div>').addClass('controller-name').text(pControllerDriveStatus.friendlyName || 'Unconfigured MachineMotion');
    lDriveStatus.append(lControllerName);
  }

  const lDriveStatusTable = $('<table>')
    .addClass('drive-status-table')
    .append(
      $('<tr>').addClass('drive-status-row').append(
        $('<th>').text('Drive Number'),
        $('<th>').text('Drive Energized'),
        $('<th>').text('Motor Detected'),
        $('<th>').text('Error(s)'),
      ),
    );

  const lDriveNumbers = Object.keys(pControllerDriveStatus.drivesEnergized);

  lDriveNumbers.forEach((pDriveNumber) => {
    const lDriveEnergized = pControllerDriveStatus.drivesEnergized[pDriveNumber];
    const lMotorDetected = pControllerDriveStatus.motorSizes[pDriveNumber] !== 'None';
    const lDriveErrors = pControllerDriveStatus.driveErrors[pDriveNumber];

    lDriveStatusTable.append(
      createDriveInformationRow(pDriveNumber, lDriveEnergized, lMotorDetected, lDriveErrors),
    );
  });

  lDriveStatus.append(lDriveStatusTable);

  return lDriveStatus;
}
